/**
 * Media Upload Utilities
 * Handles image and video file uploads with file picker triggering
 */

import { nanoid } from 'nanoid';
import { useTemplateStore } from '@/stores/designStore';
import { useToolbarStore } from '@/stores/toolbarStore';
import { useEditorStore } from '@/stores/editorStore';
import type { ImageLayer, VideoLayer, AudioLayer } from '@/types/layers';
import { createAbsolutePosition, createAlignmentPosition } from '@/types/position';

// Hidden file inputs
let imageInputElement: HTMLInputElement | null = null;
let videoInputElement: HTMLInputElement | null = null;
let audioInputElement: HTMLInputElement | null = null;

function getOrCreateImageInput(onSelect: (file: File) => void): HTMLInputElement {
  if (!imageInputElement) {
    imageInputElement = document.createElement('input');
    imageInputElement.type = 'file';
    imageInputElement.accept = 'image/*';
    imageInputElement.style.display = 'none';
    document.body.appendChild(imageInputElement);
  }

  imageInputElement.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      onSelect(file);
    }
    imageInputElement!.value = '';
  };

  return imageInputElement;
}

function getOrCreateVideoInput(onSelect: (file: File) => void): HTMLInputElement {
  if (!videoInputElement) {
    videoInputElement = document.createElement('input');
    videoInputElement.type = 'file';
    videoInputElement.accept = 'video/*';
    videoInputElement.style.display = 'none';
    document.body.appendChild(videoInputElement);
  }

  videoInputElement.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      onSelect(file);
    }
    videoInputElement!.value = '';
  };

  return videoInputElement;
}

/**
 * Trigger image upload and add to canvas at center
 */
export function triggerImageUpload() {
  const { addLayer, selectLayer } = useTemplateStore.getState();
  const { setActiveTool } = useToolbarStore.getState();
  const { selectedFormat } = useEditorStore.getState();

  const input = getOrCreateImageInput((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      if (!src) return;

      const img = new Image();
      img.onload = () => {
        // Scale down if too large
        let width = img.width;
        let height = img.height;
        const maxSize = 400;
        if (width > maxSize || height > maxSize) {
          const scale = maxSize / Math.max(width, height);
          width = width * scale;
          height = height * scale;
        }

        // Center in canvas
        const x = (selectedFormat.width - width) / 2;
        const y = (selectedFormat.height - height) / 2;

        const layer: ImageLayer = {
          id: nanoid(),
          type: 'image',
          name: file.name.split('.')[0] || 'Image',
          position: createAbsolutePosition(x, y),
          width,
          height,
          visible: true,
          locked: false,
          opacity: 1,
          content: {
            src,
            objectFit: 'cover',
          },
        };

        addLayer(layer);
        selectLayer(layer.id);
        setActiveTool('select');
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });

  input.click();
}

/**
 * Trigger video upload and add to canvas at center
 */
export function triggerVideoUpload() {
  const { addLayer, selectLayer } = useTemplateStore.getState();
  const { setActiveTool } = useToolbarStore.getState();
  const { selectedFormat } = useEditorStore.getState();

  const input = getOrCreateVideoInput((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      if (!src) return;

      // Create video element to get dimensions
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        // Scale down if too large
        let width = video.videoWidth;
        let height = video.videoHeight;
        const maxSize = 400;
        if (width > maxSize || height > maxSize) {
          const scale = maxSize / Math.max(width, height);
          width = width * scale;
          height = height * scale;
        }

        // Center in canvas
        const x = (selectedFormat.width - width) / 2;
        const y = (selectedFormat.height - height) / 2;

        const layer: VideoLayer = {
          id: nanoid(),
          type: 'video',
          name: file.name.split('.')[0] || 'Video',
          position: createAbsolutePosition(x, y),
          width,
          height,
          visible: true,
          locked: false,
          opacity: 1,
          content: {
            src,
            objectFit: 'cover',
            loop: true,
            muted: true,
            autoplay: true,
          },
        };

        addLayer(layer);
        selectLayer(layer.id);
        setActiveTool('select');
      };
      video.src = src;
    };
    reader.readAsDataURL(file);
  });

  input.click();
}

function getOrCreateAudioInput(onSelect: (file: File) => void): HTMLInputElement {
  if (!audioInputElement) {
    audioInputElement = document.createElement('input');
    audioInputElement.type = 'file';
    audioInputElement.accept = 'audio/*';
    audioInputElement.style.display = 'none';
    document.body.appendChild(audioInputElement);
  }

  audioInputElement.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      onSelect(file);
    }
    audioInputElement!.value = '';
  };

  return audioInputElement;
}

/**
 * Trigger audio upload and add as audio layer
 */
export function triggerAudioUpload() {
  const { addLayer, selectLayer } = useTemplateStore.getState();
  const { setActiveTool } = useToolbarStore.getState();

  const input = getOrCreateAudioInput((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      if (!src) return;

      // Create audio element to get duration
      const audio = new Audio();
      audio.onloadedmetadata = () => {
        const duration = audio.duration;

        const layer: AudioLayer = {
          id: nanoid(),
          type: 'audio',
          name: file.name.split('.')[0] || 'Audio',
          // Audio layers don't have a canvas position, but BaseLayer requires it
          position: createAlignmentPosition('Center'),
          visible: true,
          locked: false,
          opacity: 1,
          content: {
            src,
            name: file.name,
            loop: false,
            volume: 1,
            duration,
          },
        };

        addLayer(layer);
        selectLayer(layer.id);
        setActiveTool('select');
      };
      audio.onerror = () => {
        // If metadata loading fails, still create the layer without duration
        const layer: AudioLayer = {
          id: nanoid(),
          type: 'audio',
          name: file.name.split('.')[0] || 'Audio',
          position: createAlignmentPosition('Center'),
          visible: true,
          locked: false,
          opacity: 1,
          content: {
            src,
            name: file.name,
            loop: false,
            volume: 1,
          },
        };

        addLayer(layer);
        selectLayer(layer.id);
        setActiveTool('select');
      };
      audio.src = src;
    };
    reader.readAsDataURL(file);
  });

  input.click();
}
